/**
 * TaskTimesEditor — supervisor/admin override of a task's recorded execution
 * times (started_at / completed_at), e.g. fixing a forgotten Start or Finish
 * that inflated the elapsed clock. Precise time inputs are deliberate here: the
 * common fix is a gross correction (a 139-hour clock down to the real ~2 hours),
 * where typing the actual time beats dragging across a multi-hour bar. The
 * per-stage *drag* timeline lives on the project-detail row, where reallocating
 * stage time is the point.
 *
 * Saves via the supervisor/admin PATCH /tasks/:id (started_at/completed_at); the
 * server validates completed_at ≥ started_at. The task-detail timer recomputes
 * from the refreshed task.
 */
import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useUpdateTask } from '@/api/tasks';
import { Button } from '@/components/ui/Button';
import { countedWorkedMinutes, localScheduledDays } from '@/lib/laborDuration';

/** datetime-local value (local) → ISO (UTC), or null when blank/invalid. */
function fromLocalInput(v: string): string | null {
  if (!v) return null;
  const d = new Date(v); // interpreted in the browser's local timezone
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const TWO_HOURS_MS = 2 * 60 * 60_000;

/** First candidate ISO that parses to a finite instant, as epoch-ms, else null. */
function firstMs(...candidates: Array<string | null | undefined>): number | null {
  for (const c of candidates) {
    if (!c) continue;
    const ms = parseISO(c).getTime();
    if (Number.isFinite(ms)) return ms;
  }
  return null;
}

/**
 * Seed the Started/Completed inputs. A task completed without a recorded timer
 * (started_at null) still gets an editable window: finish from completed_at (or
 * the scheduled end), start from the scheduled start — or two hours before the
 * finish — so the supervisor edits from a real point instead of a blank field.
 */
function seedInputs(
  startedAt: string | null,
  completedAt: string | null,
  scheduledStart?: string | null,
  scheduledEnd?: string | null,
): { start: string; finish: string } {
  const finishMs = firstMs(completedAt, scheduledEnd, scheduledStart);
  const startCandidate = firstMs(startedAt, scheduledStart);
  const startMs =
    startCandidate != null && (finishMs == null || startCandidate <= finishMs)
      ? startCandidate
      : finishMs != null
        ? finishMs - TWO_HOURS_MS
        : null;
  return {
    start: startMs != null ? format(new Date(startMs), "yyyy-MM-dd'T'HH:mm") : '',
    finish: finishMs != null ? format(new Date(finishMs), "yyyy-MM-dd'T'HH:mm") : '',
  };
}

/** HH:MM:SS for an elapsed duration. */
function fmtDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
}

export function TaskTimesEditor({
  taskId,
  startedAt,
  completedAt,
  scheduledStart,
  scheduledEnd,
  onClose,
}: {
  taskId: string;
  startedAt: string | null;
  completedAt: string | null;
  /** Fallback anchors when a task was completed without a recorded timer. */
  scheduledStart?: string | null;
  scheduledEnd?: string | null;
  onClose: () => void;
}) {
  const update = useUpdateTask(taskId);
  const seed = seedInputs(startedAt, completedAt, scheduledStart, scheduledEnd);
  const [start, setStart] = useState(() => seed.start);
  const [finish, setFinish] = useState(() => seed.finish);

  const startMs = start ? new Date(start).getTime() : NaN;
  const finishMs = finish ? new Date(finish).getTime() : NaN;
  const haveBoth = !Number.isNaN(startMs) && !Number.isNaN(finishMs);
  const ordered = haveBoth && finishMs >= startMs;

  async function save() {
    if (!ordered || update.isPending) return;
    try {
      await update.mutateAsync({
        started_at: fromLocalInput(start),
        completed_at: fromLocalInput(finish),
      });
      toast.success('Times updated');
      onClose();
    } catch (err) {
      toast.error(`Couldn't update times: ${(err as Error).message}`);
    }
  }

  return (
    <div className="rounded-2xl border border-line bg-bg p-4 space-y-3 text-left">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-widest text-ink-faint">Adjust times</span>
        <span className="text-[11px] text-ink-faint">Supervisor override</span>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs text-ink-dim">Started</span>
          <input
            type="datetime-local"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="mt-1 w-full rounded-lg border border-line bg-fill-soft p-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </label>
        <label className="block">
          <span className="text-xs text-ink-dim">Completed</span>
          <input
            type="datetime-local"
            value={finish}
            onChange={(e) => setFinish(e.target.value)}
            className="mt-1 w-full rounded-lg border border-line bg-fill-soft p-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </label>
      </div>
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm">
          {ordered ? (
            <>
              <span className="text-ink-dim">Counts</span>{' '}
              <span className="font-semibold tabular-nums">
                {fmtDuration(
                  countedWorkedMinutes(
                    startMs,
                    finishMs,
                    undefined,
                    localScheduledDays(scheduledStart, scheduledEnd),
                  ) * 60_000,
                )}
              </span>
            </>
          ) : haveBoth ? (
            <span className="text-status-blocked-fg">Finish must be on or after start.</span>
          ) : (
            <span className="text-ink-faint">Set both a start and a finish.</span>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => void save()} disabled={!ordered || update.isPending}>
            {update.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
